import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatOpeningBalance } from "../../helpers/financeAccounts";
import {
  loadFinanceAccountOrder,
  resolveFinanceAccountOrder,
  saveFinanceAccountOrder,
} from "../../helpers/financeAccountOrder";
import {
  buildFinanceAccountCardMetrics,
  groupFinanceAccountsForSummary,
} from "../../helpers/financePosition";
import {
  FINANCE_CARD_STYLE,
  FINANCE_CARD_TITLE_STYLE,
  FINANCE_COMING_SOON_STYLE,
} from "./financeUi";
import "./FinanceAccountsSummary.css";

const ACCOUNT_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: "12px",
};

const ACCOUNT_CARD_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: "14px",
  padding: "14px 16px",
  minWidth: 0,
};

const METRIC_ROW = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: "10px",
  marginTop: "8px",
};

function getAccountId(account) {
  return account?.accountId || account?.id || null;
}

function AccountCardBody({ account, currency, compact }) {
  const metrics =
    Array.isArray(account.metrics) && account.metrics.length > 0
      ? account.metrics
      : buildFinanceAccountCardMetrics(account, {
          salesTaxHeld: account.salesTaxHeld,
        });
  const accountCurrency = account.currency || currency;

  return (
    <>
      <p
        style={{
          margin: "0 0 4px",
          fontSize: compact ? "14px" : "15px",
          fontWeight: 700,
          letterSpacing: "0.01em",
          paddingRight: "28px",
        }}
      >
        {account.name}
      </p>
      {metrics.map((metric) => {
        const isBalance = metric.key === "balance";
        const isTax =
          metric.key === "sales_tax_held" || metric.key === "gst_hst_held";
        const isLiabilityTone = account.class === "liability" && isBalance;
        return (
          <div key={metric.key} style={METRIC_ROW}>
            <span
              style={{
                fontSize: "11px",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                fontWeight: 700,
                opacity: 0.55,
              }}
            >
              {metric.label}
            </span>
            <span
              style={{
                fontSize: isBalance
                  ? compact
                    ? "15px"
                    : "16px"
                  : compact
                    ? "13px"
                    : "14px",
                fontWeight: isBalance ? 800 : 700,
                color: isLiabilityTone
                  ? "rgba(248,113,113,0.95)"
                  : isTax
                    ? "#c9a84c"
                    : metric.key === "available"
                      ? "#4ee2a1"
                      : "rgba(255,255,255,0.95)",
                whiteSpace: "nowrap",
              }}
            >
              {formatOpeningBalance(metric.value, accountCurrency)}
            </span>
          </div>
        );
      })}
    </>
  );
}

function SortableAccountCard({ id, compact, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        ...ACCOUNT_CARD_STYLE,
        padding: compact ? "12px 14px" : ACCOUNT_CARD_STYLE.padding,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={[
        "finance-account-card",
        "finance-account-card--sortable",
        isDragging ? "finance-account-card--dragging" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-finance-account={id}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="finance-account-card__handle"
        aria-label="Drag to reorder account"
        {...attributes}
        {...listeners}
      >
        <span aria-hidden="true">⋮⋮</span>
      </button>
      {children}
    </div>
  );
}

function Header({
  title,
  compact,
  embedded,
  onManageAccounts,
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "12px",
        marginBottom: compact ? "10px" : "14px",
      }}
    >
      <div>
        <h3 style={{ ...FINANCE_CARD_TITLE_STYLE, margin: 0 }}>{title}</h3>
        {!compact ? (
          <p
            style={{
              margin: "4px 0 0",
              fontSize: "12px",
              opacity: 0.55,
            }}
          >
            Where your money is — and what it represents
          </p>
        ) : null}
      </div>
      {typeof onManageAccounts === "function" ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onManageAccounts();
          }}
          style={{
            border: "none",
            background: "transparent",
            color: "#4ee2a1",
            fontWeight: 700,
            fontSize: "12px",
            cursor: "pointer",
            padding: 0,
            whiteSpace: "nowrap",
          }}
        >
          {compact || embedded ? "Manage →" : "Manage accounts →"}
        </button>
      ) : null}
    </div>
  );
}

function TotalsFooter({ summary, currency, compact }) {
  return (
    <div
      style={{
        marginTop: compact ? "12px" : "16px",
        paddingTop: "12px",
        borderTop: "1px solid rgba(201,168,76,0.25)",
        display: "flex",
        flexWrap: "wrap",
        gap: "10px 18px",
      }}
    >
      <div style={{ display: "flex", gap: "10px", alignItems: "baseline" }}>
        <span style={{ fontSize: "13px", fontWeight: 700 }}>Cash total</span>
        <span
          style={{
            fontSize: compact ? "15px" : "16px",
            fontWeight: 800,
            color: "#4ee2a1",
          }}
        >
          {formatOpeningBalance(summary.cashTotal, currency)}
        </span>
      </div>
      {summary.liabilityTotal > 0 ? (
        <div style={{ display: "flex", gap: "10px", alignItems: "baseline" }}>
          <span style={{ fontSize: "13px", fontWeight: 700 }}>
            Credit & loans
          </span>
          <span
            style={{
              fontSize: compact ? "14px" : "15px",
              fontWeight: 800,
              color: "#f87171",
            }}
          >
            {formatOpeningBalance(summary.liabilityTotal, currency)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Where-is-my-money account cards (responsive wrap).
 * Metrics are data-driven per account purpose via buildFinanceAccountCardMetrics.
 * Optional drag-and-drop reorder (Finance dashboard only) persists like dashboard widgets.
 *
 * @param {"full"|"compact"} variant
 * @param {boolean} embedded — omit outer card chrome when nested in another card
 * @param {boolean} reorderable — enable DnD when true (desktop Finance Accounts)
 */
export function FinanceAccountsSummary({
  accounts = [],
  currency = "CAD",
  loading = false,
  variant = "full",
  embedded = false,
  reorderable = false,
  currentUser = null,
  onManageAccounts,
  title = "Accounts",
  desk = false,
}) {
  const summary = useMemo(
    () => groupFinanceAccountsForSummary(accounts),
    [accounts],
  );
  const compact = variant === "compact";
  const shellStyle = desk
    ? {}
    : embedded
    ? {
        paddingTop: compact ? "4px" : "8px",
        borderTop: "1px solid rgba(255,255,255,0.10)",
      }
    : FINANCE_CARD_STYLE;

  const accountById = useMemo(() => {
    const map = new Map();
    for (const account of accounts || []) {
      const id = getAccountId(account);
      if (id) {
        map.set(String(id), account);
      }
    }
    return map;
  }, [accounts]);

  const availableIds = useMemo(
    () =>
      (accounts || [])
        .map((account) => getAccountId(account))
        .filter(Boolean)
        .map(String),
    [accounts],
  );

  const [order, setOrder] = useState(() =>
    reorderable
      ? loadFinanceAccountOrder(currentUser, availableIds)
      : availableIds,
  );

  useEffect(() => {
    if (!reorderable) {
      setOrder(availableIds);
      return;
    }
    setOrder(loadFinanceAccountOrder(currentUser, availableIds));
  }, [reorderable, currentUser, availableIds.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleOrder = useMemo(
    () => resolveFinanceAccountOrder(order, availableIds),
    [order, availableIds],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const persistOrder = (nextOrder) => {
    const resolved = saveFinanceAccountOrder(
      currentUser,
      nextOrder,
      availableIds,
    );
    setOrder(resolved);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = visibleOrder.indexOf(String(active.id));
    const newIndex = visibleOrder.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    persistOrder(arrayMove(visibleOrder, oldIndex, newIndex));
  };

  if (loading) {
    return (
      <div style={shellStyle}>
        {!embedded ? <h3 style={FINANCE_CARD_TITLE_STYLE}>{title}</h3> : null}
        <p style={FINANCE_COMING_SOON_STYLE}>Loading…</p>
      </div>
    );
  }

  if (!summary.accountCount) {
    return (
      <div style={shellStyle}>
        {desk ? null : (
          <Header
            title={title}
            compact={compact}
            embedded={embedded}
            onManageAccounts={onManageAccounts}
          />
        )}
        <p style={{ ...FINANCE_COMING_SOON_STYLE, margin: 0 }}>
          No accounts yet. Create an account to start tracking where your money
          is.
        </p>
      </div>
    );
  }

  const cards = visibleOrder.map((accountId) => {
    const account = accountById.get(accountId);
    if (!account) {
      return null;
    }

    const body = (
      <AccountCardBody
        account={account}
        currency={currency}
        compact={compact}
      />
    );

    if (reorderable) {
      return (
        <SortableAccountCard key={accountId} id={accountId} compact={compact}>
          {body}
        </SortableAccountCard>
      );
    }

    return (
      <div
        key={accountId}
        style={{
          ...ACCOUNT_CARD_STYLE,
          padding: compact ? "12px 14px" : ACCOUNT_CARD_STYLE.padding,
        }}
        className="finance-account-card"
      >
        {body}
      </div>
    );
  });

  return (
    <div style={shellStyle} className={desk ? "fin-acc-desk" : undefined}>
      {desk ? null : (
        <Header
          title={title}
          compact={compact}
          embedded={embedded}
          onManageAccounts={onManageAccounts}
        />
      )}

      {reorderable ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleOrder}
            strategy={rectSortingStrategy}
          >
            <div style={ACCOUNT_GRID} className="finance-accounts-grid">
              {cards}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div style={ACCOUNT_GRID} className="finance-accounts-grid">
          {cards}
        </div>
      )}

      <TotalsFooter summary={summary} currency={currency} compact={compact} />
    </div>
  );
}
